import { client, OutboundTaskTypeKey } from '../../api';
import type { Task } from './types';
import toast from 'react-hot-toast';
import { StandardErrorToast, Toast } from 'design-system/src/components/Toast/Toast';
import { tasksQueryKey } from '../../queries';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import {
  BenefitsRoutingConfirmationActions,
  BenefitsRoutingSelectionActions,
  FormActionContainer,
  OfferedBestCashPriceActions,
  PharmacyRequestedActions,
} from './actions';
import { Typography } from 'design-system/src/components/Typography/Typography';

type TaskActionsProps = {
  setShowDeclineModal: (show: boolean) => void;
  task: Task;
  setTaskResponseLoading: (loading: boolean) => void;
  setShowNewFollowupTaskModal: (show: boolean) => void;
  rxId: string;
  drugName: string;
  drugNdc?: string;
};

const TaskActions = ({
  setShowDeclineModal,
  setShowNewFollowupTaskModal,
  task,
  setTaskResponseLoading,
  rxId,
  drugName,
  drugNdc,
}: TaskActionsProps) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const handleTaskResponse = async (response: string) => {
    try {
      const formData = new FormData();
      formData.append('task_response', response);
      setTaskResponseLoading(true);
      await client.post(`tasks/${task.id}/respond`, formData);
      toast.custom(() => <Toast variant="success">Task response received.</Toast>);
      navigate('/tasks');
      queryClient.invalidateQueries({
        queryKey: tasksQueryKey(),
      });
    } catch (error) {
      toast.custom(() => <StandardErrorToast />);
      console.error(error);
      Sentry.captureException(error);
    } finally {
      setTaskResponseLoading(false);
    }
  };

  switch (task.task_key) {
    case OutboundTaskTypeKey.OFFERED_BEST_CASH_PRICE:
      return (
        <OfferedBestCashPriceActions
          setShowDeclineModal={setShowDeclineModal}
          setShowNewFollowupTaskModal={setShowNewFollowupTaskModal}
          handleTaskResponse={handleTaskResponse}
          cashPricePharmacySuggestions={task.cash_price_pharmacy_suggestions ?? []}
        />
      );

    case OutboundTaskTypeKey.PHARMACY_REQUESTED:
      return (
        <PharmacyRequestedActions
          setShowDeclineModal={setShowDeclineModal}
          setShowNewFollowupTaskModal={setShowNewFollowupTaskModal}
          handleTaskResponse={handleTaskResponse}
          rxId={rxId}
        />
      );

    case OutboundTaskTypeKey.BENEFITS_ROUTING_CONFIRMATION:
      return (
        <BenefitsRoutingConfirmationActions
          taskId={task.id}
          task={task}
          setShowNewFollowupTaskModal={setShowNewFollowupTaskModal}
        />
      );

    case OutboundTaskTypeKey.BENEFITS_ROUTING_SELECTION:
      return (
        <BenefitsRoutingSelectionActions
          taskId={task.id}
          task={task}
          drugName={drugName}
          drugNdc={drugNdc}
          setShowNewFollowupTaskModal={setShowNewFollowupTaskModal}
        />
      );

    default:
      return (
        <FormActionContainer>
          <Typography styledAs="bodySmallDMSans">
            Unable to load actions for this task. Please try refreshing the page.
          </Typography>
        </FormActionContainer>
      );
  }
};

export default TaskActions;
